<script setup>
import { computed } from "vue";
import { useModalogStore } from "@/stores/modalog";
import { useInertiaAction } from "@/composables/useInertiaAction.js";
import { useToastStore } from "@/stores/toast.js";
import BrandConfirmModalog from "@/Components/Modalogs/BrandConfirmModalog.vue";

// Pinia Stores
const modalogStore = useModalogStore();
const toastStore = useToastStore();

// State: busy spans the delete request and the follow-up reload (GH-1159),
// so the modalog reads "Deleting…" until the list is actually gone.
const { busy: isProcessing, run: runDelete } = useInertiaAction();

// Props
const props = defineProps({
    list: {
        type: Object,
        default: () => ({}),
    },
    shouldRedirect: {
        type: Boolean,
        default: false,
    },
});

// Computed
const listTypeTitleText = computed(() => {
    return props.list.type.charAt(0).toUpperCase() + props.list.type.substr(1);
});

// Methods
/**
 * Delete a wish list.
 */
async function deleteWishList() {
    const listName = props.list.name;
    const request = () =>
        axios.delete(route("giftlist.destroy", { giftlist: props.list.uuid }));

    if (props.shouldRedirect) {
        // Full page load ahead — no partial reload, park the toast for the
        // next mount.
        try {
            await request();
            toastStore.flashNext("success", `"${listName}" was deleted.`);
            window.location = route("user.giftlists");
        } catch (error) {
            console.error(error);
            modalogStore.closeModalog();
        }

        return;
    }

    await runDelete(request, {
        // My Lists props plus the dashboard sidebar's `lists` (BH-29);
        // Inertia drops the names a page doesn't have.
        only: ["expertlists", "giftlists", "lists"],
        onSuccess: () => {
            toastStore.success(`"${listName}" was deleted.`);
            props.list.is_processing = true;
            props.list.is_processing_text = `${listName} Deleted`;
        },
    });

    // Closes after the reload has swapped the list out (or after a failed
    // request), so nothing flashes back to its old state.
    modalogStore.closeModalog();
}
</script>

<template>
    <BrandConfirmModalog
        :title="`Delete ${listTypeTitleText} List`"
        confirm-label="Delete"
        busy-label="Deleting"
        danger
        :busy="isProcessing"
        @confirm="deleteWishList"
    >
        <p>
            Are you sure you want to delete
            <strong>{{ list.name }}</strong> from your wish lists?
        </p>
    </BrandConfirmModalog>
</template>
